import { Stack, Typography } from "@mui/material";
import { useSocialUserSearchQuery } from "Api/Queries/Social/useSocialUserSearchQuery";
import { SocialSummaryRow } from "Components/Social/SocialSummaryRow";
import {
  HANDLE_MIN_LENGTH,
  normalizeHandleSearchTerm,
} from "Schemas/handleSchema";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.People;

type Props = {
  searchTerm: string;
  debouncedSearchTerm: string;
};

/**
 * The state machine of a people search: hint below the minimum length, loading,
 * no results, or the result rows. The host owns the input and the debounce.
 */
export const UserSearchResults = ({
  searchTerm,
  debouncedSearchTerm,
}: Props) => {
  const { t } = useResource();
  const { data: results, isFetching } =
    useSocialUserSearchQuery(debouncedSearchTerm);

  // While below the minimum, no search fires and the hint explains what to type.
  if (normalizeHandleSearchTerm(searchTerm).length < HANDLE_MIN_LENGTH) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(Resources.Common.SearchMinCharsHint, {
          minChars: HANDLE_MIN_LENGTH,
        })}
      </Typography>
    );
  }

  if (results?.length) {
    return (
      <Stack gap={2}>
        {results.map((result) => (
          <SocialSummaryRow key={result.identityUserPublicID} user={result} />
        ))}
      </Stack>
    );
  }

  // Loading covers every wait with nothing better to show: the debounce window
  // (typed term not yet the queried term), the fetch itself, and a refetch after
  // an empty result — so the searchable state never renders empty and the
  // content below doesn't jump.
  const isWaitingForResults =
    isFetching ||
    normalizeHandleSearchTerm(searchTerm) !==
      normalizeHandleSearchTerm(debouncedSearchTerm);

  if (isWaitingForResults) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(Resources.Common.Loading)}
      </Typography>
    );
  }

  if (results) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(RESOURCES.SearchNoResults)}
      </Typography>
    );
  }

  return null;
};
